import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface Statistic {
  gameType: string;
  gamesPlayed: number;
  gamesWon: number;
  gamesLost: number;
  attemptsDistribution: { [key: number]: number };
}

const Statistics: React.FC = () => {
  const [statistics, setStatistics] = useState<Statistic[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    try {
      const response = await fetch(`${API_URL}/games/statistics`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setStatistics(data.statistics || []);
      }
    } catch (err) {
      console.error('Error loading statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  const getGameTypeName = (gameType: string) => {
    switch (gameType) {
      case 'daily':
        return 'Tägliches Frördle';
      case 'free':
        return 'Freies Frördlen';
      case 'multiplayer':
        return 'Multiplayer';
      default:
        return gameType;
    }
  };

  const getWinRate = (stat: Statistic) => {
    if (stat.gamesPlayed === 0) return 0;
    return Math.round((stat.gamesWon / stat.gamesPlayed) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
        <div className="text-white text-xl">Lädt...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 p-4">
      <div className="max-w-4xl mx-auto pt-8">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <h1 className="text-3xl font-bold text-center mb-8 text-gray-800">Statistiken</h1>

          {statistics.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">Noch keine Statistiken verfügbar.</p>
              <p className="text-gray-500 text-sm">Spiele einige Spiele, um Statistiken zu sehen.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {statistics.map((stat) => (
                <div key={stat.gameType} className="border border-gray-200 rounded-lg p-6">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                    {getGameTypeName(stat.gameType)}
                  </h2>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">Spiele gespielt</p>
                      <p className="text-2xl font-bold text-blue-600">{stat.gamesPlayed}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">Gewonnen</p>
                      <p className="text-2xl font-bold text-green-600">{stat.gamesWon}</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">Verloren</p>
                      <p className="text-2xl font-bold text-red-600">{stat.gamesLost}</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">Gewinnrate</p>
                      <p className="text-2xl font-bold text-purple-600">{getWinRate(stat)}%</p>
                    </div>
                  </div>

                  {stat.gameType !== 'multiplayer' && Object.keys(stat.attemptsDistribution).length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-3">Verteilung der Versuche</h3>
                      <div className="space-y-2">
                        {[1, 2, 3, 4, 5].map((attempts) => {
                          const count = stat.attemptsDistribution[attempts] || 0;
                          const total = Object.values(stat.attemptsDistribution).reduce((a, b) => a + b, 0);
                          const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                          
                          return (
                            <div key={attempts} className="flex items-center gap-4">
                              <div className="w-16 text-sm font-medium text-gray-700">
                                {attempts} {attempts === 1 ? 'Versuch' : 'Versuche'}
                              </div>
                              <div className="flex-1 bg-gray-200 rounded-full h-6 relative overflow-hidden">
                                <div
                                  className="bg-blue-500 h-full rounded-full transition-all duration-300 flex items-center justify-end pr-2"
                                  style={{ width: `${percentage}%` }}
                                >
                                  {count > 0 && (
                                    <span className="text-xs text-white font-semibold">{count}</span>
                                  )}
                                </div>
                              </div>
                              <div className="w-12 text-sm text-gray-600 text-right">
                                {percentage}%
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/menu')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
            >
              Zurück zum Menü
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistics;

